import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { requestAppeal } from "services/advocateComplaintService";
import { AdvocateComplaints, Prosecutor } from "types";
import { LoadingIcon } from "utils/constant";
import { APPEAL_STATUS, APPEAL_TYPE } from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: AdvocateComplaints;
  onCompliantRefresh: any;
  defaultAppealType?: "inspection_to_court" | "inspection_to_council";
  isAppealToGeneralCouncil?: boolean;
  isAppealToCourt?: boolean;
}

export default function AddAppealToCourtModal({
  isOpen,
  setIsOpen,
  complaintData,
  onCompliantRefresh,
  defaultAppealType,
  isAppealToGeneralCouncil = false,
  isAppealToCourt = false,
}: ModalProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const [formState, setFormState] = useState({
    memorandum_summary: "",
    appeal_type: defaultAppealType ?? APPEAL_TYPE.ACCUSED_PROSECUTOR_TO_COURT,
    requesting_prosecutor: null as Prosecutor | null,
    discipline_case: null,
    previous_decision: null,
    files: [] as File[],
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!complaintData?.uu_id) {
      showToast(t("compliantNotFound"), "error");
      return;
    }

    if (formState?.files?.length <= 0) {
      showToast(t("memorandumFileIsRequired"), "error");
      return;
    }

    const data = new FormData();
    data.append("memorandum_summary", formState.memorandum_summary);
    data.append("memorandum_document", formState?.files[0] || "");
    data.append("appeal_type", defaultAppealType || formState.appeal_type);
    data.append("appeal_status", APPEAL_STATUS.PENDING);
    data.append("requesting_prosecutor", user?.full_name || "");
    data.append("userid_requesting_prosecutor", user?.user_id);
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );
    data.append("discipline_case", String(complaintData?.uu_id));
    data.append(
      "is_appeal_to_general_council",
      String(isAppealToGeneralCouncil),
    );
    data.append("is_appeal_to_court", String(isAppealToCourt));

    const isFormData = true;

    setIsSubmitLoading(true);
    try {
      await requestAppeal(data, isFormData);

      onCompliantRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");

      setFormState({
        memorandum_summary: "",
        appeal_type: APPEAL_TYPE.INSPECTION_PROSECUTOR_TO_COUNCIL_OFFICE,
        requesting_prosecutor: null as Prosecutor | null,
        discipline_case: null,
        previous_decision: null,
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("uploadMemorandumOfDisciplinaryAppeal")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          {!defaultAppealType && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                {t("appealType")}
              </label>
              <select
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                name="appeal_type"
                value={formState.appeal_type}
                onChange={handleChange}
              >
                <option value={"inspection_to_council"}>
                  {t("appealFromInspectionProsecutorToCouncilOffice")}
                </option>
                <option value={"inspection_to_court"}>
                  {t("appealFromInspectionProsecutorToCourt")}
                </option>
              </select>
            </div>
          )}

          <div className="">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("amendedMemorandumOfDisciplinaryAppealSummary")}
            </label>
            <textarea
              required
              rows={6}
              name="memorandum_summary"
              value={formState.memorandum_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("amendedMemorandumOfDisciplinaryAppealDoc")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState.files.length > 0 && (
              <div className="file-list">
                {formState.files.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={isSubmitLoading}
        >
          {isSubmitLoading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("uploadMemorandum")
          )}
        </button>
      </form>
    </SideModal>
  );
}
